import React, { useMemo, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Col, Form, Row, Table } from "react-bootstrap";
import { FiArrowDown, FiArrowUp, FiCheck, FiChevronDown, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import type { FinanceDeductible } from "@features/admin/finance/deductible/types/financeDeductible";
import type { KpiOperation } from "@features/admin/kpi/types/kpiEntryMode";
import BindingCellsEditor from "@features/presentationLayout/components/BindingCellsEditor";
import {
  COLUMN_TYPES,
  COLUMN_TYPE_LABELS,
  DISTRIBUTION_ENGINE_LABELS,
  ORIGIN_SCOPE_LABELS,
  SECTION_TYPES,
  SECTION_TYPE_LABELS,
  TOTALS_MODE_LABELS,
  type ColumnType,
  type LayoutMetaTemplate,
  type PresentationLayoutMeta,
  type TotalsMode,
} from "@features/presentationLayout/types/presentationLayout";
import {
  ARIA_BINDING_ROW_ACTIONS,
  ARIA_COLLAPSE_SECTION,
  ARIA_DONE_EDITING_CELLS,
  ARIA_EDIT_CELLS,
  ARIA_EXPAND_SECTION,
  ARIA_MOVE_ROW_DOWN,
  ARIA_MOVE_ROW_UP,
  ARIA_MOVE_SECTION_DOWN,
  ARIA_MOVE_SECTION_UP,
  ARIA_REMOVE_ROW,
  ARIA_REMOVE_SECTION,
  LAYOUT_BUILDER_SECTION_BODY_TEST_ID_SUFFIX,
  LAYOUT_BUILDER_SECTION_COLLAPSE_TEST_ID_SUFFIX,
  LAYOUT_BUILDER_SECTION_TEST_ID,
  MAX_LABEL_LENGTH,
  MAX_OPERANDS_PER_COLUMN,
  MESSAGE_LABEL_REQUIRED,
  PLACEHOLDER_COLUMN_LABEL_DEDUCTIBLE,
  PLACEHOLDER_COLUMN_LABEL_KPI,
  PLACEHOLDER_COLUMN_LABEL_REQUIRED,
  PLACEHOLDER_ROW_LABEL_ACTIVITY,
  PLACEHOLDER_ROW_LABEL_KPI,
  PLACEHOLDER_ROW_LABEL_REQUIRED,
} from "@features/presentationLayout/constants/presentationLayoutConstants";
import {
  bindingRowSummary,
  defaultForSaveColumn,
  defaultForSaveRow,
  engineForKpi,
  formulaHint,
  kpiPickerOptions,
  labelFieldKey,
  matrixColumnKpis,
  moveItem,
  newColumn,
  newRow,
  removeColumnFromSection,
  requiresExactlyTwo,
  swapColumnsInSection,
  toggleOperand,
  usingLabelText,
  withColumns,
  type BindingCatalog,
  type DraftColumn,
  type DraftRow,
  type DraftSection,
} from "@features/presentationLayout/utils/layoutForm";

type Props = {
  section: DraftSection;
  index: number;
  count: number;
  meta: PresentationLayoutMeta;
  catalog: BindingCatalog;
  templates: LayoutMetaTemplate[];
  deductibles: FinanceDeductible[];
  disabled: boolean;
  fieldErrors: Record<string, string>;
  onClearFieldError: (key: string) => void;
  onChange: (section: DraftSection) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
};

const toId = (value: string): number => Number(value) || 0;

const SectionBuilder: React.FC<Props> = ({
  section,
  index,
  count,
  meta,
  catalog,
  templates,
  deductibles,
  disabled,
  fieldErrors = {},
  onClearFieldError,
  onChange,
  onMove,
  onRemove,
}) => {
  const type = section.section_type;
  const isBinding = type === SECTION_TYPES.BINDING_TABLE;
  const hasColumns = type !== SECTION_TYPES.FIELD_LIST;
  const columnTypes = meta.section_column_types[type] ?? [];
  const totalsModes = meta.section_totals_modes[type] ?? [];
  const matrixKpis = useMemo(
    () => (type === SECTION_TYPES.KPI_MATRIX ? matrixColumnKpis(section, catalog) : []),
    [type, section, catalog]
  );
  const allChurchRows = section.rows.every((row) =>
    row.report_template_id !== null && catalog.template(row.report_template_id)?.origin_scope === "CHURCH"
  );
  const deductibleNames = useMemo(
    () => new Map(deductibles.map((d) => [d.deductible_id, d.name])),
    [deductibles]
  );
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  const patch = (changes: Partial<DraftSection>) => onChange({ ...section, ...changes });
  const patchRow = (rowIndex: number, changes: Partial<DraftRow>) => {
    if (Object.prototype.hasOwnProperty.call(changes, "label")) {
      onClearFieldError(labelFieldKey(index, "rows", rowIndex));
    }
    patch({ rows: section.rows.map((row, i) => (i === rowIndex ? { ...row, ...changes } : row)) });
  };
  const patchColumn = (columnIndex: number, changes: Partial<DraftColumn>) => {
    if (Object.prototype.hasOwnProperty.call(changes, "label")) {
      onClearFieldError(labelFieldKey(index, "columns", columnIndex));
    }
    const columns = section.columns.map((column, i) => (i === columnIndex ? { ...column, ...changes } : column));
    onChange(withColumns(section, columns));
  };

  const rowLabelPlaceholder = isBinding
    ? PLACEHOLDER_ROW_LABEL_REQUIRED
    : type === SECTION_TYPES.KPI_MATRIX
      ? PLACEHOLDER_ROW_LABEL_ACTIVITY
      : PLACEHOLDER_ROW_LABEL_KPI;

  const columnLabelPlaceholder = (column: DraftColumn): string => {
    if (isBinding) return PLACEHOLDER_COLUMN_LABEL_REQUIRED;
    if (type === SECTION_TYPES.DISTRIBUTION_TABLE && column.column_type === COLUMN_TYPES.DEDUCTIBLE) {
      return PLACEHOLDER_COLUMN_LABEL_DEDUCTIBLE;
    }
    if (type === SECTION_TYPES.KPI_MATRIX && column.column_type === COLUMN_TYPES.KPI_BINDING) {
      return PLACEHOLDER_COLUMN_LABEL_KPI;
    }
    return PLACEHOLDER_COLUMN_LABEL_REQUIRED;
  };

  const onToggleOperand = (columnIndex: number, operand: number) => {
    patchColumn(columnIndex, { operands: toggleOperand(section.columns[columnIndex].operands, operand) });
  };

  const prefix = `${LAYOUT_BUILDER_SECTION_TEST_ID}-${index}`;
  const bodyId = `${prefix}-${LAYOUT_BUILDER_SECTION_BODY_TEST_ID_SUFFIX}`;
  const sectionTitle = section.title.trim() || `Section ${index + 1}`;

  return (
    <Card className="mb-3" data-testid={prefix}>
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <button
          type="button"
          className="d-inline-flex align-items-center gap-2 border-0 bg-transparent p-0 text-start"
          aria-expanded={expanded}
          aria-controls={bodyId}
          aria-label={expanded ? ARIA_COLLAPSE_SECTION : ARIA_EXPAND_SECTION}
          data-testid={`${prefix}-${LAYOUT_BUILDER_SECTION_COLLAPSE_TEST_ID_SUFFIX}`}
          onClick={() => setExpanded((open) => !open)}
        >
          <FiChevronDown
            style={{ transform: expanded ? "rotate(180deg)" : undefined }}
            aria-hidden
          />
          <Badge bg="info">{SECTION_TYPE_LABELS[type]}</Badge>
          <span className="fw-semibold text-body">{sectionTitle}</span>
        </button>
        <ButtonGroup size="sm">
          <Button
            variant="outline-secondary"
            aria-label={ARIA_MOVE_SECTION_UP}
            disabled={disabled || index === 0}
            onClick={() => onMove(index - 1)}
          >
            <FiArrowUp aria-hidden />
          </Button>
          <Button
            variant="outline-secondary"
            aria-label={ARIA_MOVE_SECTION_DOWN}
            disabled={disabled || index === count - 1}
            onClick={() => onMove(index + 1)}
          >
            <FiArrowDown aria-hidden />
          </Button>
          <Button
            variant="outline-danger"
            aria-label={ARIA_REMOVE_SECTION}
            disabled={disabled}
            onClick={onRemove}
          >
            <FiTrash2 aria-hidden />
          </Button>
        </ButtonGroup>
      </Card.Header>
      {expanded ? (
      <Card.Body id={bodyId} data-testid={bodyId}>
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Label className="small fw-semibold">Title <span className="text-danger">*</span></Form.Label>
            <Form.Control
              size="sm"
              value={section.title}
              maxLength={MAX_LABEL_LENGTH}
              disabled={disabled}
              onChange={(e) => patch({ title: e.target.value })}
              data-testid={`${prefix}-title`}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small fw-semibold">Row header label</Form.Label>
            <Form.Control
              size="sm"
              value={section.row_header_label ?? ""}
              maxLength={MAX_LABEL_LENGTH}
              disabled={disabled}
              onChange={(e) => patch({ row_header_label: e.target.value })}
            />
          </Col>
          <Col md={6}>
            <Form.Label className="small fw-semibold">Totals</Form.Label>
            <Form.Select
              size="sm"
              value={section.totals_mode}
              disabled={disabled || totalsModes.length <= 1}
              onChange={(e) => patch({ totals_mode: e.target.value as TotalsMode })}
              data-testid={`${prefix}-totals`}
            >
              {totalsModes.map((mode) => (
                <option key={mode} value={mode}>{TOTALS_MODE_LABELS[mode]}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small fw-semibold">Totals label</Form.Label>
            <Form.Control
              size="sm"
              value={section.totals_label ?? ""}
              placeholder="TOTAL"
              maxLength={MAX_LABEL_LENGTH}
              disabled={disabled || section.totals_mode === "NONE"}
              onChange={(e) => patch({ totals_label: e.target.value })}
            />
          </Col>
        </Row>

        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="fw-semibold small">Rows</span>
          <Button
            size="sm"
            variant="outline-primary"
            disabled={disabled || section.rows.length >= meta.limits.rows_per_section}
            onClick={() => patch({ rows: [...section.rows, newRow(section.columns)] })}
            data-testid={`${prefix}-add-row`}
          >
            <FiPlus className="me-1" aria-hidden /> Add row
          </Button>
        </div>
        {section.rows.length === 0 ? (
          <p className="text-muted small">No rows yet.</p>
        ) : isBinding ? (
          <div className="mb-3">
            {section.rows.map((row, rowIndex) => {
              const editing = editingRowKey === row.key;
              const rowLabelKey = labelFieldKey(index, "rows", rowIndex);
              const rowLabelInvalid = !!fieldErrors[rowLabelKey];
              return (
                <div key={row.key} className="border rounded p-2 mb-2" data-testid={`${prefix}-binding-row-${rowIndex}`}>
                  <div className="d-flex align-items-start gap-2">
                    <div className="flex-grow-1">
                      <Form.Control
                        size="sm"
                        value={row.label}
                        placeholder={rowLabelPlaceholder}
                        maxLength={MAX_LABEL_LENGTH}
                        disabled={disabled}
                        isInvalid={rowLabelInvalid}
                        onChange={(e) => patchRow(rowIndex, { label: e.target.value })}
                        aria-label="Row label"
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors[rowLabelKey] || MESSAGE_LABEL_REQUIRED}
                      </Form.Control.Feedback>
                    </div>
                    <ButtonGroup size="sm" aria-label={ARIA_BINDING_ROW_ACTIONS}>
                      <Button
                        variant="outline-secondary"
                        disabled={disabled}
                        aria-label={editing ? ARIA_DONE_EDITING_CELLS : ARIA_EDIT_CELLS}
                        onClick={() => setEditingRowKey(editing ? null : row.key)}
                        data-testid={`${prefix}-edit-cells-${rowIndex}`}
                      >
                        {editing ? <FiCheck aria-hidden /> : <FiEdit2 aria-hidden />}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        aria-label={ARIA_MOVE_ROW_UP}
                        disabled={disabled || rowIndex === 0}
                        onClick={() => patch({ rows: moveItem(section.rows, rowIndex, rowIndex - 1) })}
                      >
                        <FiArrowUp aria-hidden />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        aria-label={ARIA_MOVE_ROW_DOWN}
                        disabled={disabled || rowIndex === section.rows.length - 1}
                        onClick={() => patch({ rows: moveItem(section.rows, rowIndex, rowIndex + 1) })}
                      >
                        <FiArrowDown aria-hidden />
                      </Button>
                      <Button
                        variant="outline-danger"
                        aria-label={ARIA_REMOVE_ROW}
                        disabled={disabled}
                        onClick={() => {
                          if (editingRowKey === row.key) setEditingRowKey(null);
                          patch({ rows: section.rows.filter((_, i) => i !== rowIndex) });
                        }}
                      >
                        <FiTrash2 aria-hidden />
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div className="text-muted small mt-1" data-testid={`${prefix}-row-summary-${rowIndex}`}>
                    {bindingRowSummary(row, section.columns, catalog) || "No Value columns yet."}
                  </div>
                  {editing ? (
                    <div className="mt-2">
                      <BindingCellsEditor
                        row={row}
                        columns={section.columns}
                        catalog={catalog}
                        templates={templates}
                        disabled={disabled}
                        testIdPrefix={`${prefix}-row-${rowIndex}`}
                        onChange={(cells) => patchRow(rowIndex, { cells })}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <Table size="sm" responsive className="align-middle mb-3">
            <thead>
              <tr>
                <th>Label</th>
                <th>Template</th>
                <th>Activity</th>
                {type !== SECTION_TYPES.KPI_MATRIX ? <th>KPI</th> : null}
                <th />
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, rowIndex) => {
                const activities = row.report_template_id !== null
                  ? catalog.template(row.report_template_id)?.activities ?? []
                  : [];
                const kpiOptions = row.report_template_id !== null && row.activity_id !== null
                  ? kpiPickerOptions(catalog.activity(row.report_template_id, row.activity_id)?.kpis ?? [], row.kpi_id)
                  : [];
                return (
                  <tr key={row.key}>
                    <td>
                      {(() => {
                        const rowLabelKey = labelFieldKey(index, "rows", rowIndex);
                        const usingId = `${prefix}-row-${rowIndex}-using`;
                        const defaultName = defaultForSaveRow(type, row, catalog);
                        const showUsing = row.label.trim() === "" && defaultName !== null;
                        return (
                          <>
                            <Form.Control
                              size="sm"
                              value={row.label}
                              placeholder={rowLabelPlaceholder}
                              maxLength={MAX_LABEL_LENGTH}
                              disabled={disabled}
                              isInvalid={!!fieldErrors[rowLabelKey]}
                              aria-describedby={showUsing ? usingId : undefined}
                              onChange={(e) => patchRow(rowIndex, { label: e.target.value })}
                            />
                            {showUsing ? (
                              <div id={usingId} className="text-muted small mt-1" data-testid={usingId}>
                                {usingLabelText(defaultName)}
                              </div>
                            ) : null}
                            <Form.Control.Feedback type="invalid">
                              {fieldErrors[rowLabelKey] || MESSAGE_LABEL_REQUIRED}
                            </Form.Control.Feedback>
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.report_template_id ?? ""}
                        disabled={disabled}
                        onChange={(e) => patchRow(rowIndex, { report_template_id: toId(e.target.value) || null, activity_id: null, kpi_id: null })}
                        aria-label="Template"
                      >
                        <option value="">Select…</option>
                        {templates.map((template) => (
                          <option key={template.report_template_id} value={template.report_template_id}>
                            {template.report_template_name} ({ORIGIN_SCOPE_LABELS[template.origin_scope]})
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.activity_id ?? ""}
                        disabled={disabled || activities.length === 0}
                        onChange={(e) => patchRow(rowIndex, { activity_id: toId(e.target.value) || null, kpi_id: null })}
                        aria-label="Activity"
                      >
                        <option value="">Select…</option>
                        {activities.map((activity) => (
                          <option key={activity.activity_id} value={activity.activity_id}>{activity.activity_name}</option>
                        ))}
                      </Form.Select>
                    </td>
                    {type !== SECTION_TYPES.KPI_MATRIX ? (
                      <td>
                        <Form.Select
                          size="sm"
                          value={row.kpi_id ?? ""}
                          disabled={disabled || kpiOptions.length === 0}
                          onChange={(e) => patchRow(rowIndex, { kpi_id: toId(e.target.value) || null })}
                          aria-label="KPI"
                        >
                          <option value="">Select…</option>
                          {kpiOptions.map(({ kpi, disabled: unavailable }) => (
                            <option key={kpi.kpi_id} value={kpi.kpi_id} disabled={unavailable}>{kpi.kpi_name}</option>
                          ))}
                        </Form.Select>
                        {type === SECTION_TYPES.DISTRIBUTION_TABLE && row.kpi_id !== null ? (
                          <div className="text-muted small mt-1" data-testid={`${prefix}-row-${rowIndex}-engine`}>
                            Uses {DISTRIBUTION_ENGINE_LABELS[engineForKpi(row.kpi_id, meta.tithes_kpi_id)].toLowerCase()}
                          </div>
                        ) : null}
                      </td>
                    ) : null}
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="link" aria-label={ARIA_MOVE_ROW_UP} disabled={disabled || rowIndex === 0} onClick={() => patch({ rows: moveItem(section.rows, rowIndex, rowIndex - 1) })}>
                        <FiArrowUp aria-hidden />
                      </Button>
                      <Button size="sm" variant="link" aria-label={ARIA_MOVE_ROW_DOWN} disabled={disabled || rowIndex === section.rows.length - 1} onClick={() => patch({ rows: moveItem(section.rows, rowIndex, rowIndex + 1) })}>
                        <FiArrowDown aria-hidden />
                      </Button>
                      <Button size="sm" variant="link" className="text-danger" aria-label={ARIA_REMOVE_ROW} disabled={disabled} onClick={() => patch({ rows: section.rows.filter((_, i) => i !== rowIndex) })}>
                        <FiTrash2 aria-hidden />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {hasColumns ? (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="fw-semibold small">Columns</span>
              <div className="d-flex gap-1">
                {columnTypes.map((columnType) => (
                  <Button
                    key={columnType}
                    size="sm"
                    variant="outline-primary"
                    disabled={
                      disabled
                      || section.columns.length >= meta.limits.columns_per_section
                      || (columnType === COLUMN_TYPES.DEDUCTIBLE && !allChurchRows)
                    }
                    title={columnType === COLUMN_TYPES.DEDUCTIBLE && !allChurchRows ? "Deductible columns need every row to come from a church template." : undefined}
                    onClick={() => onChange(withColumns(section, [...section.columns, newColumn(columnType)]))}
                    data-testid={`${prefix}-add-column-${columnType}`}
                  >
                    <FiPlus className="me-1" aria-hidden /> {COLUMN_TYPE_LABELS[columnType]}
                  </Button>
                ))}
              </div>
            </div>
            {section.columns.length === 0 ? (
              <p className="text-muted small mb-0">No columns yet.</p>
            ) : (
              <Table size="sm" responsive className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Label</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {section.columns.map((column, columnIndex) => {
                    const columnLabelKey = labelFieldKey(index, "columns", columnIndex);
                    const usingId = `${prefix}-column-${columnIndex}-using`;
                    const defaultName = defaultForSaveColumn(type, column, section, catalog, deductibleNames);
                    const showUsing = column.label.trim() === "" && defaultName !== null;
                    return (
                    <tr key={column.key}>
                      <td className="text-muted small">{columnIndex + 1}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          value={column.label}
                          placeholder={columnLabelPlaceholder(column)}
                          maxLength={MAX_LABEL_LENGTH}
                          disabled={disabled}
                          isInvalid={!!fieldErrors[columnLabelKey]}
                          aria-describedby={showUsing ? usingId : undefined}
                          onChange={(e) => patchColumn(columnIndex, { label: e.target.value })}
                        />
                        {showUsing ? (
                          <div id={usingId} className="text-muted small mt-1" data-testid={usingId}>
                            {usingLabelText(defaultName)}
                          </div>
                        ) : null}
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors[columnLabelKey] || MESSAGE_LABEL_REQUIRED}
                        </Form.Control.Feedback>
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={column.column_type}
                          disabled={disabled}
                          aria-label="Column type"
                          onChange={(e) => patchColumn(columnIndex, { ...newColumn(e.target.value as ColumnType), key: column.key, label: column.label })}
                        >
                          {columnTypes.map((columnType) => (
                            <option key={columnType} value={columnType}>{COLUMN_TYPE_LABELS[columnType]}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <ColumnValueEditor
                          column={column}
                          columnIndex={columnIndex}
                          columns={section.columns}
                          meta={meta}
                          matrixKpis={matrixKpis}
                          deductibles={deductibles}
                          disabled={disabled}
                          onChange={(changes) => patchColumn(columnIndex, changes)}
                          onToggleOperand={(operand) => onToggleOperand(columnIndex, operand)}
                        />
                      </td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" variant="link" aria-label="Move column left" disabled={disabled || columnIndex === 0} onClick={() => onChange(swapColumnsInSection(section, columnIndex, columnIndex - 1))}>
                          <FiArrowUp aria-hidden />
                        </Button>
                        <Button size="sm" variant="link" aria-label="Move column right" disabled={disabled || columnIndex === section.columns.length - 1} onClick={() => onChange(swapColumnsInSection(section, columnIndex, columnIndex + 1))}>
                          <FiArrowDown aria-hidden />
                        </Button>
                        <Button size="sm" variant="link" className="text-danger" aria-label="Remove column" disabled={disabled} onClick={() => onChange(removeColumnFromSection(section, columnIndex))}>
                          <FiTrash2 aria-hidden />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </>
        ) : null}
      </Card.Body>
      ) : null}
    </Card>
  );
};

export default SectionBuilder;

type ColumnValueEditorProps = {
  column: DraftColumn;
  columnIndex: number;
  columns: DraftColumn[];
  meta: PresentationLayoutMeta;
  matrixKpis: ReturnType<typeof matrixColumnKpis>;
  deductibles: FinanceDeductible[];
  disabled: boolean;
  onChange: (changes: Partial<DraftColumn>) => void;
  onToggleOperand: (operand: number) => void;
};

function ColumnValueEditor({
  column,
  columnIndex,
  columns,
  meta,
  matrixKpis,
  deductibles,
  disabled,
  onChange,
  onToggleOperand,
}: ColumnValueEditorProps) {
  switch (column.column_type) {
    case COLUMN_TYPES.KPI_BINDING: {
      const kpiOptions = kpiPickerOptions(matrixKpis, column.kpi_id);
      return (
        <Form.Select
          size="sm"
          value={column.kpi_id ?? ""}
          disabled={disabled || kpiOptions.length === 0}
          aria-label="Column KPI"
          onChange={(e) => onChange({ kpi_id: toId(e.target.value) || null })}
        >
          <option value="">{kpiOptions.length === 0 ? "Add rows first" : "Select…"}</option>
          {kpiOptions.map(({ kpi, disabled: unavailable }) => (
            <option key={kpi.kpi_id} value={kpi.kpi_id} disabled={unavailable}>{kpi.kpi_name}</option>
          ))}
        </Form.Select>
      );
    }

    case COLUMN_TYPES.SOURCE_VALUE:
      return <span className="text-muted small">The row KPI's value</span>;

    case COLUMN_TYPES.VALUE:
      return <span className="text-muted small">Each cell picks its own template, activity, and KPI</span>;

    case COLUMN_TYPES.DEDUCTIBLE:
      return (
        <Form.Select
          size="sm"
          value={column.deductible_id ?? ""}
          disabled={disabled}
          aria-label="Deductible"
          onChange={(e) => onChange({ deductible_id: toId(e.target.value) || null })}
        >
          <option value="">Select…</option>
          {deductibles
            .filter((d) => d.is_active || d.deductible_id === column.deductible_id)
            .map((d) => (
              <option key={d.deductible_id} value={d.deductible_id} disabled={!d.is_active}>
                {d.name}{d.is_active ? "" : " (inactive)"}
              </option>
            ))}
        </Form.Select>
      );

    case COLUMN_TYPES.CALCULATED: {
      const exactlyTwo = requiresExactlyTwo(meta, column.calc_operation);
      const hint = formulaHint(column, columns);
      return (
        <div>
          <Form.Select
            size="sm"
            className="mb-1"
            value={column.calc_operation ?? ""}
            disabled={disabled}
            aria-label="Operation"
            onChange={(e) => onChange({ calc_operation: (e.target.value || null) as KpiOperation | null })}
          >
            <option value="">Operation…</option>
            {meta.calc_operations.map(({ operation }) => (
              <option key={operation} value={operation}>{operation}</option>
            ))}
          </Form.Select>
          {columnIndex === 0 ? (
            <span className="text-muted small">Add columns to the left to calculate from.</span>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {columns.slice(0, columnIndex).map((operand, operandIndex) => (
                <Form.Check
                  key={operand.key}
                  inline
                  type="checkbox"
                  id={`${column.key}-operand-${operandIndex}`}
                  label={operand.label.trim() || `Column ${operandIndex + 1}`}
                  checked={column.operands.includes(operandIndex)}
                  disabled={
                    disabled
                    || (!column.operands.includes(operandIndex) && column.operands.length >= MAX_OPERANDS_PER_COLUMN)
                  }
                  onChange={() => onToggleOperand(operandIndex)}
                />
              ))}
            </div>
          )}
          <div className="text-muted small">
            {exactlyTwo ? "Choose exactly 2 columns." : "Choose 2 or more columns."}
            {hint ? <> = <span data-testid={`formula-hint-${column.key}`}>{hint}</span></> : null}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
